import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNewspaper } from "@fortawesome/pro-regular-svg-icons/faNewspaper";
import styled from "@emotion/styled";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import cn from "classnames";
import { useConfig } from "@peracto/peracto-config";
import axios from "axios";

const ScrollbarStyles = styled.div`
    ::-webkit-scrollbar {
        height: 0;
        /* height: 20px; */
    }

    /* 4px scroll thumb. 10px margin from right margin */
    /* ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 15px 15px #000;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 15px 15px #333;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-button {
        display: none;
    } */
`;

const Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faNewspaper} size="6x" />
    </div>
);

const ArticlesComponent = ({
    state: { articleCategory = null, heading = "Articles" },
    isEditMode,
    onChange,
    focused,
}) => {
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const articlesRef = useRef(null);
    const [articles, setArticles] = useState([]);
    const config = useConfig();
    const API_URL = config.get("api");

    useEffect(() => {
        const fetchArticles = async () => {
            const response = await axios.get(
                `${API_URL}/articles-by-category/${articleCategory}?itemsPerPage=${12}`
            );
            setArticles(response.data["hydra:member"]);
        };
        fetchArticles();
    }, [API_URL, articleCategory]);

    const scroll = (direction) => {
        if (articlesRef.current) {
            const { current } = articlesRef;
            const gap = 28;
            let scrollAmount = 330 + gap;
            const article = document.querySelector(
                '[data-testid="articles__item-0"]'
            );

            if (article) {
                scrollAmount = article.clientWidth + gap;
            }

            if (direction === "left") {
                current.scrollLeft -= scrollAmount;
            } else {
                current.scrollLeft += scrollAmount;
            }
        }
    };

    const checkScrollPosition = () => {
        if (articlesRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                articlesRef.current;

            setIsAtStart(scrollLeft <= 0);
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
        }
    };

    useEffect(() => {
        checkScrollPosition();
        const scrollDiv = articlesRef.current;

        if (scrollDiv) {
            scrollDiv.addEventListener("scroll", checkScrollPosition);
            window.addEventListener("resize", checkScrollPosition);
            return () => {
                scrollDiv.removeEventListener("scroll", checkScrollPosition);
                window.removeEventListener("resize", checkScrollPosition);
            };
        }
    }, []);

    return (
        <>
            <div className="flex flex-col items-center justify-between md:flex-row mb-[30px]">
                <h2
                    data-testid="product-relations__title"
                    className={
                        "text-center !text-[30px] !leading-[34px] text-brand-black !lg:text-[40px] !lg:leading-[46px] !mb-0"
                    }
                >
                    {heading}
                </h2>

                <div className="hidden gap-[10px] md:flex items-center">
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtStart,
                            }
                        )}
                        onClick={() => scroll("left")}
                        disabled={isAtStart}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtEnd,
                            }
                        )}
                        onClick={() => scroll("right")}
                        disabled={isAtEnd}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            {articles?.length > 0 ? (
                <ScrollbarStyles
                    ref={articlesRef}
                    className="flex scroll-smooth flex-row content__articles overflow-auto gap-[30px]"
                >
                    {articles.map((article, idx) => {
                        return (
                            <a
                                key={`articles_${idx}`}
                                href={`/articles/${article.slug}`}
                                onClick={(e) => {
                                    if (isEditMode) {
                                        e.preventDefault();
                                    }
                                }}
                                data-testid={`articles__item-${idx}`}
                                className={`flex content__articles__item flex-col w-[270px] min-w-[270px] max-w-[270px] lg:flex-[420px] lg:min-w-[420px] lg:max-w-[420px] gap-y-[15px] bg-brand-white`}
                            >
                                <div
                                    className="w-full relative aspect-[470/330] bg-brand-light-grey rounded-[10px] mb-[15px]"
                                    style={{
                                        backgroundImage: article.image
                                            ? `url("${article.image})"`
                                            : "none",
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                    }}
                                ></div>
                                <p
                                    className={`flex font-bold text-[24px] leading-[30px] lg:text-[30px] lg:leading-[36px] mb-0`}
                                >
                                    {article.title}
                                </p>

                                <p
                                    className="text-brand-black text-[16px] leading-[22px] mb-0"
                                    dangerouslySetInnerHTML={{
                                        __html: article.shortDescription,
                                    }}
                                />
                            </a>
                        );
                    })}
                </ScrollbarStyles>
            ) : (
                <Icon />
            )}

            <div className="mt-[15px] flex items-center justify-center gap-[10px] md:hidden">
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtStart,
                        }
                    )}
                    onClick={() => scroll("left")}
                    disabled={isAtStart}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtEnd,
                        }
                    )}
                    onClick={() => scroll("right")}
                    disabled={isAtEnd}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </>
    );
};

export default ArticlesComponent;
